import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, MaskLinesInView } from "./motion";

const VILLA =
  "https://customer-assets.emergentagent.com/job_montebello-lodge/artifacts/ri9bhuvk_image.png";
const SALA =
  "https://customer-assets.emergentagent.com/job_montebello-lodge/artifacts/aew5gmky_image.png";

const CHAPTERS = [
  {
    n: "01",
    title: "A Floresta",
    body: "A casa ergue-se no meio de um terreno privado repleto de árvores altas. A sensação é a de ter uma floresta inteira só para si — o telemóvel silencia-se e o vento faz-se ouvir.",
  },
  {
    n: "02",
    title: "A Casa",
    body: "Espaço para muitos, sem apertos. Cozinha totalmente equipada para longas refeições, salas amplas com lareira e chandelier, e a possibilidade rara de reunir toda a gente sob o mesmo tecto.",
  },
  {
    n: "03",
    title: "O Tempo",
    body: "A 40 minutos do Porto, a poucos passos dos vinhos do Douro e da serra. Perto de tudo, longe do ruído — para os dias que apetece que não acabem.",
  },
];

const STATS = [
  { value: "13", label: "Hóspedes" },
  { value: "7", label: "Quartos" },
  { value: "1", label: "Cozinha Ampla" },
  { value: "40'", label: "Do Porto" },
];

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      id="sobre"
      data-testid="about-section"
      ref={ref}
      className="relative py-28 lg:py-40 bg-cream"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Left: manifesto */}
          <div className="lg:col-span-6">
            <Reveal>
              <div className="flex items-center gap-4 mb-8">
                <span className="h-px w-14 bg-forest/40" />
                <p className="overline">Sobre o Alojamento</p>
              </div>
            </Reveal>

            <h2 className="font-display text-4xl sm:text-5xl lg:text-[4.4rem] leading-[1.0] tracking-tight text-forest font-light">
              <MaskLinesInView
                lines={["Uma casa entre árvores,", <span key="i" className="italic">um tempo só vosso.</span>]}
              />
            </h2>

            <div className="mt-14 space-y-0">
              {CHAPTERS.map((c, i) => (
                <Reveal key={c.n} delay={i * 0.08}>
                  <div className="grid grid-cols-[auto_1fr] gap-6 py-8 border-t border-border/70">
                    <span className="font-display text-terracotta text-3xl lg:text-4xl leading-none pt-1">
                      {c.n}
                    </span>
                    <div>
                      <h3 className="font-display text-2xl lg:text-3xl text-forest">{c.title}</h3>
                      <p className="mt-3 text-muted-foreground text-lg leading-relaxed max-w-lg">
                        {c.body}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal>
              <div className="mt-12 grid grid-cols-4 gap-6 max-w-lg">
                {STATS.map((s) => (
                  <div key={s.label} data-testid={`about-stat-${s.label.toLowerCase()}`}>
                    <div className="font-display text-4xl lg:text-5xl text-forest leading-none">
                      {s.value}
                    </div>
                    <div className="mt-2 text-[0.66rem] tracking-[0.16em] uppercase text-muted-foreground leading-tight">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Right: clipped image frames */}
          <div className="lg:col-span-6 lg:sticky lg:top-28">
            <motion.div
              initial={{ clipPath: "inset(12% 12% 12% 12%)", opacity: 0.4 }}
              whileInView={{ clipPath: "inset(0% 0% 0% 0%)", opacity: 1 }}
              viewport={{ once: true, margin: "-15%" }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="relative aspect-[4/5] overflow-hidden rounded-sm bg-forest"
            >
              <motion.img
                src={VILLA}
                alt="Fachada da Casa Montebello ao sol"
                style={{ y: imgY }}
                className="absolute inset-0 h-[116%] w-full object-cover"
              />
              <div className="absolute bottom-0 inset-x-0 h-1/3 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <p className="text-cream/70 text-[0.66rem] tracking-[0.24em] uppercase">A Casa</p>
                <p className="font-display text-cream text-2xl mt-1">Felgueiras, Porto</p>
              </div>
            </motion.div>

            <div className="grid grid-cols-5 gap-4 mt-4">
              <Reveal className="col-span-3" delay={0.1}>
                <div className="aspect-[4/3] overflow-hidden rounded-sm group">
                  <img
                    src={SALA}
                    alt="Sala de estar clássica com lareira"
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]"
                  />
                </div>
              </Reveal>
              <Reveal className="col-span-2" delay={0.18}>
                <div className="h-full min-h-[120px] bg-forest text-cream p-5 rounded-sm flex flex-col justify-between">
                  <p className="overline text-cream/60">Privado</p>
                  <p className="font-display text-xl leading-tight">Terreno só vosso, entre a floresta.</p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
